import { createRoot } from "@wordpress/element";
import AIResponseModal from "./ai-response-modal";
import { $, OPTIONS, TOPIC_CONTEXT, CURRENT_USER, AI_AUTHOR_ID, EXCLUDED_ROLES } from "../utils/constants";
import { sleep } from "../utils/sleep";

type FormType = "topic" | "reply";

/**
 * Check if the current user should see the AI suggestion modal.
 * Returns false if the user is the AI Author or has an excluded role.
 */
function shouldShowAIModal(): boolean {
    // Always skip for AI Author (support agent)
    if (CURRENT_USER.ID === AI_AUTHOR_ID) {
        return false;
    }

    // Skip for users with excluded roles
    if (EXCLUDED_ROLES.length > 0 && CURRENT_USER.ROLES.length > 0) {
        const hasExcludedRole = CURRENT_USER.ROLES.some(role => EXCLUDED_ROLES.includes(role));
        if (hasExcludedRole) {
            return false;
        }
    }

    return true;
}

document.addEventListener("DOMContentLoaded", () => {
    // Handle new topic form
    handleAIResponse(".bbp-topic-form", "form#new-post", "topic");
    
    // Handle reply form (could be form#new-post or form#new-reply depending on theme)
    handleAIResponse(".bbp-reply-form", "form#new-post, form#new-reply", "reply");
});

function handleAIResponse(containerSelector: string, formSelector: string, formType: FormType) {
    const $container = $(containerSelector);
    const $form = $container?.find<HTMLFormElement>(formSelector);

    if (!$form?.length) return;

    const $modal = $("<div></div>");
    $container?.append($modal);

    $form?.on("submit", async function (e) {
        // Check the appropriate option based on form type
        const isEnabled = formType === "topic" 
            ? OPTIONS.AI_RESPONSE_TO_TOPIC 
            : OPTIONS.AI_RESPONSE_TO_REPLY;
            
        if (!isEnabled) return;

        // Skip for AI Author or users with excluded roles
        if (!shouldShowAIModal()) {
            return; // Let the form submit normally
        }
        
        // Get the content from the form
        const formData = new FormData(this);
        const content = (formData.get("bbp_topic_content") || formData.get("bbp_reply_content") || "") as string;

        // For replies, check minimum word count
        if (formType === "reply") {
            const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
            if (wordCount < OPTIONS.AI_REPLY_MIN_WORDS) {
                // Skip AI for short replies, let the form submit normally
                return;
            }
        }

        e.preventDefault();
        await sleep(100);

        createRoot($modal[0]).render(
            <AIResponseModal 
                form={this} 
                formType={formType}
                topicContext={formType === "reply" ? {
                    topicId: TOPIC_CONTEXT.TOPIC_ID,
                    topicTitle: TOPIC_CONTEXT.TOPIC_TITLE,
                    thread: TOPIC_CONTEXT.THREAD,
                } : undefined}
            />
        );
    });
}
